// ABOUTME: Report creation wizard with 4 steps.
// ABOUTME: Steps: context selection (pins + investigations), configuration, generation, and complete.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { WizardSelectionStep } from './WizardSelectionStep';
import { WizardConfigStep, type ReportConfig } from './WizardConfigStep';
import type { Report, Pin, InvestigationListItem } from '@/types/api';

interface ReportWizardProps {
  onComplete: (report: Report) => void;
  onCancel: () => void;
  /** When set, wizard starts in "edit mode" — pre-filled config, skips to configure step */
  editReportId?: string;
  editReportVersion?: number;
}

type WizardStep = 'context' | 'configure' | 'generate' | 'complete';

const STEPS: WizardStep[] = ['context', 'configure', 'generate', 'complete'];

const STEP_LABELS: Record<WizardStep, string> = {
  context: 'Select Context',
  configure: 'Configure',
  generate: 'Generate',
  complete: 'Complete',
};

export function ReportWizard({ onComplete, onCancel, editReportId, editReportVersion }: ReportWizardProps) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useApp();
  const isEditMode = !!editReportId;
  const [currentStep, setCurrentStep] = useState<WizardStep>('context');
  const [error, setError] = useState<string | null>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(isEditMode);

  // Step 1: Context Selection
  const [selectedPinIds, setSelectedPinIds] = useState<string[]>([]);
  const [selectedInvestigationIds, setSelectedInvestigationIds] = useState<string[]>([]);
  // Track all pins and investigations for use in config step
  const [allPins, setAllPins] = useState<Pin[]>([]);
  const [allInvestigations, setAllInvestigations] = useState<InvestigationListItem[]>([]);
  // Pending insight refs to resolve to pin IDs once pins are loaded (edit mode)
  const [pendingInsightRefs, setPendingInsightRefs] = useState<Array<{ message_id: string; insight_id: string }>>([]);
  // Also keep ref for the toggle logic (needs sync access)
  const allPinsRef = useRef<Pin[]>([]);

  // Step 2: Configuration
  const [config, setConfig] = useState<ReportConfig>({
    title: '',
    length: '3-5',
    tone: 'executive',
    includeExecutiveSummary: true,
    includeMethodology: false,
    includeRecommendations: true,
    includeVisuals: true,
    customPrompt: '',
  });

  // Generate default title based on selection
  useEffect(() => {
    const totalSelected = selectedPinIds.length + selectedInvestigationIds.length;
    if (totalSelected > 0 && !config.title) {
      if (totalSelected === 1) {
        setConfig((prev) => ({
          ...prev,
          title: 'Analysis Report',
        }));
      } else {
        setConfig((prev) => ({
          ...prev,
          title: `Combined Analysis Report (${totalSelected} sources)`,
        }));
      }
    }
  }, [selectedPinIds.length, selectedInvestigationIds.length, config.title]);

  // Load generation config in edit mode
  useEffect(() => {
    if (!editReportId) return;

    const loadConfig = async () => {
      try {
        if (!currentWorkspaceId) {
          setError('No workspace selected. You can still modify settings.');
          setIsLoadingConfig(false);
          return;
        }
        const genConfig = await apiClient.reports.getGenerationConfig(editReportId, currentWorkspaceId);
        if (genConfig) {
          setConfig({
            title: genConfig.title || '',
            length: genConfig.length || '3-5',
            tone: genConfig.tone || 'executive',
            includeExecutiveSummary: genConfig.include_executive_summary ?? true,
            includeMethodology: genConfig.include_methodology ?? false,
            includeRecommendations: genConfig.include_recommendations ?? true,
            includeVisuals: genConfig.include_visuals ?? true,
            customPrompt: genConfig.custom_prompt || '',
          });
          // Pre-select investigations from the config
          if (genConfig.investigation_ids?.length) {
            setSelectedInvestigationIds(genConfig.investigation_ids);
          }
          // Pre-select pins from mandatory_insight_refs — matched by message_id + insight_id
          if (genConfig.mandatory_insight_refs?.length) {
            setPendingInsightRefs(genConfig.mandatory_insight_refs);
          }
        }
        // If genConfig is null (legacy report), keep defaults — user can fill in config manually
      } catch (err) {
        console.error('Failed to load generation config:', err);
        setError('Failed to load report configuration. You can still modify settings.');
      } finally {
        setIsLoadingConfig(false);
      }
    };

    loadConfig();
  }, [editReportId]);

  // Toggle pin selection
  const togglePin = useCallback((id: string) => {
    setSelectedPinIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  // Toggle investigation selection - also deselects pins from that investigation
  const toggleInvestigation = useCallback((id: string) => {
    setSelectedInvestigationIds((prev) => {
      const isCurrentlySelected = prev.includes(id);
      if (isCurrentlySelected) {
        // Deselecting - also remove any pins from this investigation
        const pinsFromInvestigation = allPinsRef.current
          .filter((pin) => pin.source_investigation_id === id)
          .map((pin) => pin.id);
        if (pinsFromInvestigation.length > 0) {
          setSelectedPinIds((prevPins) =>
            prevPins.filter((pinId) => !pinsFromInvestigation.includes(pinId))
          );
        }
        return prev.filter((i) => i !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  // Update pins when WizardSelectionStep loads them
  const handlePinsLoaded = useCallback((pins: Pin[]) => {
    allPinsRef.current = pins;
    setAllPins(pins);
  }, []);

  // Resolve pending insight refs → pin IDs once pins are loaded (edit mode)
  useEffect(() => {
    if (pendingInsightRefs.length === 0 || allPins.length === 0) return;
    const matchedPinIds = allPins
      .filter((pin) =>
        pendingInsightRefs.some(
          (ref) => ref.message_id === pin.message_id && ref.insight_id === pin.insight_id
        )
      )
      .map((pin) => pin.id);
    if (matchedPinIds.length > 0) {
      setSelectedPinIds((prev) => {
        const combined = new Set([...prev, ...matchedPinIds]);
        return Array.from(combined);
      });
    }
    setPendingInsightRefs([]); // Clear once resolved
  }, [allPins, pendingInsightRefs]);

  // Update investigations when WizardSelectionStep loads them
  const handleInvestigationsLoaded = useCallback((investigations: InvestigationListItem[]) => {
    setAllInvestigations(investigations);
  }, []);

  // Get selected pins and investigations objects for config step
  const selectedPins = useMemo(
    () => allPins.filter(pin => selectedPinIds.includes(pin.id)),
    [allPins, selectedPinIds]
  );
  const selectedInvestigations = useMemo(
    () => allInvestigations.filter(inv => selectedInvestigationIds.includes(inv.id)),
    [allInvestigations, selectedInvestigationIds]
  );

  // Handle step navigation
  const goNext = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  };

  const goBack = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    }
  };

  // Check if can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 'context':
        return selectedPinIds.length > 0 || selectedInvestigationIds.length > 0;
      case 'configure':
        return config.title.trim().length > 0;
      default:
        return true;
    }
  };

  // Generate report
  const handleGenerate = async () => {
    if (!canProceed() || !user || !currentWorkspaceId) {
      return;
    }

    setCurrentStep('generate');
    setError(null);

    // Map user-friendly config to API format
    const reportType = config.length === '1-2' ? 'executive' : config.length === '6+' ? 'detailed' : 'technical';
    const audience = config.tone === 'executive' ? 'c-suite' : config.tone === 'technical' ? 'technical' : 'clients';

    // Build focus areas based on what sections they want
    const focusAreas: ('risks' | 'anomalies' | 'trends' | 'recommendations')[] = ['trends'];
    if (config.includeRecommendations) focusAreas.push('recommendations');

    // Build custom prompt with additional context
    let customPrompt = config.customPrompt || '';
    if (config.includeExecutiveSummary && !customPrompt.toLowerCase().includes('executive summary')) {
      customPrompt = `Include an executive summary section at the beginning. ${customPrompt}`.trim();
    }
    if (config.length === '1-2') {
      customPrompt = `Keep the report concise, around 1-2 pages. ${customPrompt}`.trim();
    } else if (config.length === '6+') {
      customPrompt = `Create a comprehensive report with detailed analysis, 6+ pages. ${customPrompt}`.trim();
    }

    try {
      // Convert selected pins to InsightReferences
      const mandatoryInsightRefs = selectedPins.map(pin => ({
        message_id: pin.message_id,
        insight_id: pin.insight_id,
      }));

      let report: Report;

      if (editReportId) {
        // Edit mode: regenerate existing report
        // Map frontend config to backend field names (same mapping as new reports)
        report = await apiClient.reports.regenerateReport(editReportId, {
          workspace_id: currentWorkspaceId,
          version: editReportVersion,
          title: config.title,
          custom_prompt: customPrompt || undefined,
          report_type: reportType,
          audience: audience,
          focus_areas: focusAreas,
          include_charts: config.includeVisuals,
          include_tables: config.includeVisuals,
          include_methodology: config.includeMethodology,
          mandatory_insight_refs: mandatoryInsightRefs,
          investigation_ids: selectedInvestigationIds,
        });
      } else {
        // New report: generate fresh
        report = await apiClient.reports.generateWithContext({
          workspace_id: currentWorkspaceId,
          title: config.title,
          mandatory_insight_refs: mandatoryInsightRefs,
          investigation_ids: selectedInvestigationIds,
          report_type: reportType,
          audience: audience,
          focus_areas: focusAreas,
          include_charts: config.includeVisuals,
          include_tables: config.includeVisuals,
          include_methodology: config.includeMethodology,
          custom_prompt: customPrompt || undefined,
        });
      }

      setCurrentStep('complete');
      onComplete(report);
    } catch (err) {
      console.error('Failed to generate report:', err);
      setError(editReportId ? 'Failed to regenerate report. Please try again.' : 'Failed to generate report. Please try again.');
      setCurrentStep('configure');
    }
  };

  // Get step progress
  const getStepIndex = () => STEPS.indexOf(currentStep);

  return (
    <div className="flex flex-col h-full">
      {/* Header with Progress */}
      <div className="p-6 border-b border-surface-200">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-surface-800">
            {isEditMode ? 'Regenerate Report' : 'Create Report'}
          </h1>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-surface-100 rounded-sm transition-colors cursor-pointer"
          >
            <X className="w-5 h-5 text-surface-500" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2">
          {STEPS.slice(0, -1).map((step, index) => (
            <div key={step} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index <= getStepIndex()
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface-100 text-surface-500'
                  }`}
                >
                  {index < getStepIndex() ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span className="text-xs text-surface-500 mt-1 hidden sm:block">
                  {STEP_LABELS[step]}
                </span>
              </div>
              {index < STEPS.length - 2 && (
                <div
                  className={`w-16 h-0.5 mx-2 transition-colors ${
                    index < getStepIndex()
                      ? 'bg-primary-600'
                      : 'bg-surface-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Context Selection */}
          {currentStep === 'context' && (
            <motion.div
              key="context"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <WizardSelectionStep
                selectedPinIds={selectedPinIds}
                selectedInvestigationIds={selectedInvestigationIds}
                onPinToggle={togglePin}
                onInvestigationToggle={toggleInvestigation}
                onPinsLoaded={handlePinsLoaded}
                onInvestigationsLoaded={handleInvestigationsLoaded}
              />
            </motion.div>
          )}

          {/* Step 2: Configuration */}
          {currentStep === 'configure' && (
            <motion.div
              key="configure"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {isLoadingConfig ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-primary-600 animate-spin mb-3" />
                  <p className="text-sm text-surface-600">Loading report configuration...</p>
                </div>
              ) : (
                <WizardConfigStep
                  config={config}
                  onChange={setConfig}
                  selectedPins={selectedPins}
                  selectedInvestigations={selectedInvestigations}
                  onEditContext={isEditMode ? undefined : goBack}
                />
              )}

              {error && (
                <div className="mt-4 p-4 bg-error-light border border-error/30 rounded-sm">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Generation */}
          {currentStep === 'generate' && (
            <motion.div
              key="generate"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex flex-col items-center justify-center py-16"
            >
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
              <h2 className="text-lg font-medium text-surface-800 mb-2">
                {isEditMode ? 'Regenerating Report...' : 'Generating Report...'}
              </h2>
              <p className="text-sm text-surface-600 text-center max-w-md">
                AI is synthesizing insights from{' '}
                {selectedPinIds.length > 0 && (
                  <>
                    {selectedPinIds.length} pin{selectedPinIds.length !== 1 ? 's' : ''}
                  </>
                )}
                {selectedPinIds.length > 0 && selectedInvestigationIds.length > 0 && ' and '}
                {selectedInvestigationIds.length > 0 && (
                  <>
                    {selectedInvestigationIds.length} investigation
                    {selectedInvestigationIds.length !== 1 ? 's' : ''}
                  </>
                )}{' '}
                into your report.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer Navigation */}
      {currentStep !== 'generate' && currentStep !== 'complete' && (
        <div className="p-6 border-t border-surface-200 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={currentStep === 'context' ? onCancel : (isEditMode ? onCancel : goBack)}
            leftIcon={<ChevronLeft className="w-4 h-4" />}
          >
            {currentStep === 'context' ? 'Cancel' : (isEditMode ? 'Cancel' : 'Back')}
          </Button>

          <Button
            onClick={currentStep === 'configure' ? handleGenerate : goNext}
            disabled={!canProceed()}
            rightIcon={<ChevronRight className="w-4 h-4" />}
            data-tour={currentStep === 'context' ? 'wizard-next' : undefined}
          >
            {currentStep === 'configure'
              ? (isEditMode ? 'Regenerate Report' : 'Generate Report')
              : 'Next'}
          </Button>
        </div>
      )}
    </div>
  );
}
